import { supabase } from '../lib/supabase';

// Helper: wrap any promise with a timeout
function withTimeout(promise, ms = 10000) {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`Zahtev je prekoračio vreme (${ms / 1000}s). Proverite internet vezu i pokušajte ponovo.`)), ms)
  );
  return Promise.race([promise, timeout]);
}

export const offerService = {
  async createOffer(formData, userId) {
    console.log('[offerService.createOffer] START');

    if (!userId) throw new Error('Niste prijavljeni');

    const wishTitle = (formData?.voucherName || '')?.trim();

    const insertData = {
      user_id: userId,
      wish_type: formData?.wishType || 'prodaja',
      voucher_name: formData?.voucherName || null,
      voucher_symbol: formData?.voucherSymbol || null,
      wish_title: wishTitle || formData?.voucherName || null,
      token_quantity: formData?.tokenQuantity ? parseInt(formData?.tokenQuantity) : null,
      max_price: formData?.maxPrice ? parseFloat(formData?.maxPrice) : null,
      currency: formData?.currency || 'RSD',
      description: formData?.description || null,
      special_conditions: formData?.specialConditions || null,
      exchange_wishes: formData?.exchangeWishes || null,
      expiry_date: formData?.expiryDate || null,
      category: formData?.category || null,
      is_active: true,
      offer_status: 'active',
    };

    console.log('[offerService.createOffer] Payload ready:', JSON.stringify(insertData));

    const { error } = await supabase?.from('marketplace_wishes')?.insert(insertData);

    if (error) {
      console.error('[offerService.createOffer] INSERT ERROR:', error?.code, error?.message);
      if (error?.code === '42501' || error?.message?.includes('policy')) throw new Error('Nemate dozvolu za objavljivanje. Proverite da li ste prijavljeni.');
      if (error?.code === '23505') throw new Error('Oglas sa ovim podacima već postoji.');
      throw new Error(`Greška baze: ${error.message}`);
    }

    console.log('[offerService.createOffer] SUCCESS! Offer inserted.');
    return { ...insertData, id: null };
  },

  async getAllOffers(filters = {}) {
    console.log('[offerService.getAllOffers] START - filters:', JSON.stringify(filters));

    try {
      let query = supabase
        ?.from('marketplace_wishes')
        ?.select('*')
        ?.eq('is_active', true)
        ?.eq('offer_status', 'active')
        ?.order('created_at', { ascending: false });

      if (filters?.wishType) {
        query = query?.eq('wish_type', filters?.wishType);
      }
      if (filters?.currency) {
        query = query?.eq('currency', filters?.currency);
      }
      if (filters?.minPrice !== undefined && filters?.minPrice !== '') {
        query = query?.gte('max_price', parseFloat(filters?.minPrice));
      }
      if (filters?.maxPrice !== undefined && filters?.maxPrice !== '') {
        query = query?.lte('max_price', parseFloat(filters?.maxPrice));
      }
      if (filters?.search) {
        query = query?.or(`voucher_name.ilike.*${filters?.search}*,voucher_symbol.ilike.*${filters?.search}*`);
      }

      console.log('[offerService.getAllOffers] Executing Supabase query...');
      const { data, error, status, statusText } = await query;

      console.log('[offerService.getAllOffers] Response - status:', status, statusText, '| error:', error, '| rows:', data?.length);

      if (error) {
        console.error('[offerService.getAllOffers] SUPABASE ERROR:', error?.code, error?.message, error?.details, error?.hint);
        throw new Error(`Greška baze: ${error.message}`);
      }

      if (!data || data?.length === 0) {
        console.warn('[offerService.getAllOffers] Zero rows returned.');
        return [];
      }

      const result = data?.map(r => ({
        id: r?.id,
        userId: r?.user_id,
        wishType: r?.wish_type,
        voucherName: r?.voucher_name,
        voucherSymbol: r?.voucher_symbol,
        wishTitle: r?.wish_title,
        tokenQuantity: r?.token_quantity,
        maxPrice: r?.max_price,
        currency: r?.currency,
        description: r?.description || r?.detailed_requirements,
        specialConditions: r?.special_conditions,
        exchangeWishes: r?.exchange_wishes,
        expiryDate: r?.expiry_date,
        images: r?.images || [],
        logoUrl: r?.logo_url,
        isActive: r?.is_active,
        offerStatus: r?.offer_status || 'active',
        createdAt: r?.created_at,
        source: 'marketplace_wishes',
      }));

      console.log('[offerService.getAllOffers] SUCCESS - returning', result?.length, 'offers');
      return result;
    } catch (err) {
      console.error('[offerService.getAllOffers] EXCEPTION:', err?.message, err);
      throw err;
    }
  },

  // Admin: get all offers including archived/expired
  async getAllOffersAdmin(filters = {}) {
    console.log('[offerService.getAllOffersAdmin] START');
    try {
      let query = supabase
        ?.from('marketplace_wishes')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.offerStatus) {
        query = query?.eq('offer_status', filters?.offerStatus);
      }
      if (filters?.wishType) {
        query = query?.eq('wish_type', filters?.wishType);
      }
      if (filters?.search) {
        query = query?.or(`voucher_name.ilike.*${filters?.search}*,voucher_symbol.ilike.*${filters?.search}*`);
      }

      const { data, error } = await query;
      if (error) throw new Error(`Greška baze: ${error.message}`);

      return (data || [])?.map(r => ({
        id: r?.id,
        userId: r?.user_id,
        wishType: r?.wish_type,
        voucherName: r?.voucher_name,
        voucherSymbol: r?.voucher_symbol,
        wishTitle: r?.wish_title,
        tokenQuantity: r?.token_quantity,
        maxPrice: r?.max_price,
        currency: r?.currency,
        description: r?.description || r?.detailed_requirements,
        specialConditions: r?.special_conditions,
        exchangeWishes: r?.exchange_wishes,
        expiryDate: r?.expiry_date,
        images: r?.images || [],
        logoUrl: r?.logo_url,
        isActive: r?.is_active,
        offerStatus: r?.offer_status || 'active',
        createdAt: r?.created_at,
        source: 'marketplace_wishes',
      }));
    } catch (err) {
      console.error('[offerService.getAllOffersAdmin] EXCEPTION:', err?.message);
      throw err;
    }
  },

  // Archive an offer (set is_active=false, offer_status='archived')
  async archiveOffer(offerId) {
    const { error } = await supabase
      ?.from('marketplace_wishes')
      ?.update({ is_active: false, offer_status: 'archived' })
      ?.eq('id', offerId);
    if (error) throw new Error(error.message);
  },

  // Mark expired offers (called on page load)
  async markExpiredOffers() {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { error } = await supabase
        ?.from('marketplace_wishes')
        ?.update({ is_active: false, offer_status: 'expired' })
        ?.eq('offer_status', 'active')
        ?.eq('is_active', true)
        ?.lt('expiry_date', today)
        ?.not('expiry_date', 'is', null);
      if (error) console.warn('[offerService.markExpiredOffers] Error:', error?.message);
    } catch (err) {
      console.warn('[offerService.markExpiredOffers] Exception:', err?.message);
    }
  },

  // Fallback: archive offers whose negotiation completed (in case DB trigger missed it)
  async archiveCompletedNegotiationOffers() {
    try {
      const { error } = await supabase?.rpc('archive_offers_with_completed_negotiations');
      if (error) console.warn('[offerService.archiveCompletedNegotiationOffers] Error:', error?.message);
    } catch (err) {
      console.warn('[offerService.archiveCompletedNegotiationOffers] Exception:', err?.message);
    }
  },

  // Get active negotiation count for a specific offer
  async getActiveNegotiationForOffer(offerId) {
    try {
      const { data, error } = await supabase
        ?.from('negotiations')
        ?.select('id, status')
        ?.eq('offer_id', offerId)
        ?.in('status', ['initiated', 'in_progress', 'pending'])
        ?.limit(1);
      if (error) return null;
      return data?.length > 0 ? data?.[0] : null;
    } catch {
      return null;
    }
  },

  // Batch: get active negotiations for multiple offer IDs
  async getActiveNegotiationsForOffers(offerIds) {
    if (!offerIds?.length) return {};
    try {
      const { data, error } = await supabase
        ?.from('negotiations')
        ?.select('id, offer_id, status')
        ?.in('offer_id', offerIds)
        ?.in('status', ['initiated', 'in_progress', 'pending']);
      if (error) return {};
      const map = {};
      (data || [])?.forEach(n => {
        if (!map?.[n?.offer_id]) map[n?.offer_id] = n;
      });
      return map;
    } catch {
      return {};
    }
  },

  async getOfferById(id) {
    const { data, error } = await supabase?.from('marketplace_wishes')?.select('*')?.eq('id', id)?.single();

    if (error) throw new Error(error.message || 'Greška pri učitavanju');
    if (!data) throw new Error('Oglas nije pronađen');

    return {
      id: data?.id,
      userId: data?.user_id,
      wishType: data?.wish_type,
      voucherName: data?.voucher_name,
      voucherSymbol: data?.voucher_symbol,
      wishTitle: data?.wish_title,
      tokenQuantity: data?.token_quantity,
      maxPrice: data?.max_price,
      currency: data?.currency,
      description: data?.description || data?.detailed_requirements,
      specialConditions: data?.special_conditions,
      exchangeWishes: data?.exchange_wishes,
      expiryDate: data?.expiry_date,
      images: data?.images || [],
      logoUrl: data?.logo_url,
      isActive: data?.is_active,
      offerStatus: data?.offer_status || 'active',
      createdAt: data?.created_at,
      source: 'marketplace_wishes',
    };
  },
};
